import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import CandidateSilhouetteGuide from "../components/CandidateSilhouetteGuide";
import { getCameraStream } from "../lib/videoCapture";
import { loadHtmlToImage } from "../lib/loadHtmlToImage";
import { ensureCompositeFontsReady } from "../lib/compositeFonts";
import { primeAudioForPlayback } from "../lib/playAudio";
import "../play.css";
import { Camera, Mic, AlertTriangle, PlayCircle } from "lucide-react";

const CameraSetup = () => {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const { token, streamRef } = useGame();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  useEffect(() => {
    const el = videoRef.current;
    const stream = streamRef.current;
    if (ready && el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [ready, streamRef]);

  const requestCamera = async () => {
    setError("");
    try {
      await primeAudioForPlayback();
      const stream = await getCameraStream();
      streamRef.current = stream;
      setReady(true);
      Promise.all([loadHtmlToImage(), ensureCompositeFontsReady()]).catch(() => {});
    } catch {
      setError("Impossible d'accéder à la caméra. Autorisez caméra et micro dans le navigateur.");
      toast.error("Accès caméra refusé");
    }
  };

  const handleMainAction = async () => {
    if (!ready) {
      requestCamera();
      return;
    }
    await primeAudioForPlayback();
    navigate("/play");
  };

  return (
    <PageLayout>
      <div className="max-w-md mx-auto flex flex-col gap-8">
        <div className="fade-up text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Activez votre caméra</h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Fonctionne sur smartphone ou ordinateur. Le studio enregistre toute votre partie en format
            vertical.
          </p>
          <div className="mt-4 mx-auto max-w-lg rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-left text-amber-100">
            <span className="font-bold">Important !</span> Un seul candidat à l&apos;écran. Cadrez-vous bien
            dans la silhouette pendant toute la partie.
          </div>
          <div className="mt-4 mx-auto max-w-lg space-y-3 text-left">
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-1">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                1
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Appuyez sur <strong className="text-white">« Activer caméra &amp; micro »</strong> et
                acceptez les permissions. Une silhouette apparaît pour vous cadrer.
              </p>
            </div>
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                2
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Tenez le téléphone (ou posez l&apos;écran) en <strong className="text-white">vertical</strong>,
                face à vous, à hauteur des épaules.
              </p>
            </div>
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                3
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Remplissez la silhouette (tête et épaules bien visibles).
              </p>
            </div>
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                4
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Restez à bonne distance pour que le micro capte clairement vos réponses.
              </p>
            </div>
          </div>
        </div>

        <div className="md-card overflow-hidden border border-md-cyan/15 aspect-[9/16] relative fade-up w-full max-h-[min(70dvh,640px)] mx-auto">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-cover play-video-mirror transition-opacity duration-500 ${ready ? "opacity-100" : "opacity-0"}`}
            data-testid="camera-preview"
          />
          {ready && <CandidateSilhouetteGuide />}
          {!ready && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40">
              <Camera size={72} strokeWidth={1} />
              <div className="mt-4 font-semibold text-lg uppercase tracking-widest">Caméra inactive</div>
            </div>
          )}
        </div>

        {error && (
          <div className="fade-up text-sm text-red-400 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <MdGlowButton
          variant={ready ? "success" : "default"}
          onClick={handleMainAction}
          wrapClassName="mt-0"
          data-testid={ready ? "start-game-button" : "enable-camera-button"}
        >
          {ready ? (
            <>
              <PlayCircle className="w-5 h-5 shrink-0" />
              Je suis prêt — Lancer le jeu
            </>
          ) : (
            <>
              <Camera className="w-5 h-5 shrink-0" />
              Activer caméra & micro
              <Mic className="w-5 h-5 shrink-0" />
            </>
          )}
        </MdGlowButton>

        {ready && (
          <p className="text-sm text-emerald-400/90 text-center fade-up">
            Caméra OK — appuyez sur le bouton vert pour lancer le jeu.
          </p>
        )}
      </div>
    </PageLayout>
  );
};

export default CameraSetup;
